import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/hooks/useProfile";

export function useOrganization() {
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const orgId = profile?.organization_id;

  return useQuery({
    queryKey: ["organization", orgId],
    queryFn: async () => {
      if (!orgId || !user) return null;
      // Try full table access (admins have policy for this)
      const { data, error } = await supabase
        .from("organizations")
        .select("*")
        .eq("id", orgId)
        .single();
      if (!error && data) return data;
      // Fall back to safe RPC for non-admin members
      const { data: rpcData, error: rpcError } = await supabase
        .rpc("get_member_org_info", { _user_id: user.id });
      if (rpcError) throw rpcError;
      const row = rpcData?.[0];
      if (!row) return null;
      return {
        id: row.id,
        name: row.name,
        tier: row.tier,
        max_seats: row.max_seats,
        subscription_status: row.subscription_status,
        billing_email: null,
        stripe_customer_id: null,
        created_at: "",
        updated_at: "",
      };
    },
    enabled: !!orgId,
  });
}

export function useOrgRole() {
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const orgId = profile?.organization_id;

  const { data: membership } = useQuery({
    queryKey: ["org-membership", orgId, user?.id],
    queryFn: async () => {
      if (!orgId || !user?.id) return null;
      const { data, error } = await supabase
        .from("organization_members")
        .select("role, status")
        .eq("organization_id", orgId)
        .eq("user_id", user.id)
        .eq("status", "active")
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!orgId && !!user?.id,
  });

  return {
    role: membership?.role as "owner" | "admin" | "member" | null,
    isAdmin: membership?.role === "owner" || membership?.role === "admin",
    isOwner: membership?.role === "owner",
    isMember: !!membership,
    orgId,
  };
}

export function useOrgMembers() {
  const { orgId, isAdmin } = useOrgRole();

  return useQuery({
    queryKey: ["org-members", orgId],
    queryFn: async () => {
      if (!orgId) return [];
      const { data: membersData, error } = await supabase
        .from("organization_members")
        .select("*")
        .eq("organization_id", orgId)
        .order("created_at", { ascending: true });
      if (error) throw error;

      // Fetch profiles for active members
      const userIds = (membersData || []).filter(m => m.user_id).map(m => m.user_id!);
      let profilesMap: Record<string, any> = {};
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("user_id, full_name")
          .in("user_id", userIds);
        (profiles || []).forEach(p => { profilesMap[p.user_id] = p; });
      }

      return (membersData || []).map(m => ({
        ...m,
        profiles: m.user_id ? profilesMap[m.user_id] || null : null,
      }));
    },
    enabled: !!orgId && isAdmin,
  });
}

export function useInviteMember() {
  const queryClient = useQueryClient();
  const { orgId } = useOrgRole();

  return useMutation({
    mutationFn: async (input: string | { email: string; role?: "member" | "admin" }) => {
      if (!orgId) throw new Error("No organization");
      const email = typeof input === "string" ? input : input.email;
      const role = typeof input === "string" ? "member" : (input.role || "member");
      const { data, error } = await supabase
        .from("organization_members")
        .insert({
          organization_id: orgId,
          invited_email: email.toLowerCase(),
          role,
          status: "pending",
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org-members", orgId] });
    },
  });
}

export function useUpdateMemberRole() {
  const queryClient = useQueryClient();
  const { orgId } = useOrgRole();

  return useMutation({
    mutationFn: async ({ memberId, role }: { memberId: string; role: "member" | "admin" }) => {
      if (!orgId) throw new Error("No organization");
      const { error } = await supabase
        .from("organization_members")
        .update({ role })
        .eq("id", memberId)
        .eq("organization_id", orgId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org-members", orgId] });
    },
  });
}

export function useRemoveMember() {
  const queryClient = useQueryClient();
  const { orgId } = useOrgRole();

  return useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await supabase
        .from("organization_members")
        .delete()
        .eq("id", memberId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org-members", orgId] });
    },
  });
}

export function useTaskAssignments() {
  const { orgId, isAdmin } = useOrgRole();
  const { user } = useAuth();

  return useQuery({
    queryKey: ["task-assignments", orgId, user?.id],
    queryFn: async () => {
      if (!orgId) return [];
      let query = supabase
        .from("task_assignments")
        .select("*")
        .eq("organization_id", orgId)
        .order("created_at", { ascending: false });

      if (!isAdmin) {
        query = query.eq("assigned_to", user!.id);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: !!orgId && !!user?.id,
  });
}

export function useCreateAssignment() {
  const queryClient = useQueryClient();
  const { orgId } = useOrgRole();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (assignment: { assigned_to: string; title: string; description?: string; due_date?: string }) => {
      if (!orgId || !user?.id) throw new Error("No organization");
      const { data, error } = await supabase
        .from("task_assignments")
        .insert({
          organization_id: orgId,
          assigned_by: user.id,
          ...assignment,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task-assignments"] });
    },
  });
}

export function usePendingInvites() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["pending-invites", user?.email],
    queryFn: async () => {
      if (!user?.email) return [];
      const { data, error } = await supabase
        .from("organization_members")
        .select("*, organizations(name)")
        .eq("invited_email", user.email.toLowerCase())
        .eq("status", "pending");
      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.email,
  });
}

export function useAcceptInvite() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (inviteId: string) => {
      if (!user?.id) throw new Error("Not authenticated");

      // Get the invite to find org_id
      const { data: invite, error: fetchError } = await supabase
        .from("organization_members")
        .select("organization_id")
        .eq("id", inviteId)
        .single();
      if (fetchError) throw fetchError;

      // Update the member row
      const { error: updateError } = await supabase
        .from("organization_members")
        .update({
          user_id: user.id,
          status: "active",
          accepted_at: new Date().toISOString(),
        })
        .eq("id", inviteId);
      if (updateError) throw updateError;

      // Link profile to org
      const { error: profileError } = await supabase
        .from("profiles")
        .update({ organization_id: invite.organization_id })
        .eq("user_id", user.id);
      if (profileError) throw profileError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-invites"] });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      queryClient.invalidateQueries({ queryKey: ["org-membership"] });
    },
  });
}

export function useTransferOwnership() {
  const queryClient = useQueryClient();
  const { orgId } = useOrgRole();

  return useMutation({
    mutationFn: async (newOwnerUserId: string) => {
      if (!orgId) throw new Error("No organization");
      const { error } = await supabase.rpc("transfer_ownership", {
        _org_id: orgId,
        _new_owner_user_id: newOwnerUserId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org-members", orgId] });
      queryClient.invalidateQueries({ queryKey: ["org-membership"] });
    },
  });
}
